import type React from 'react'

import { AuthContext, type StandaloneSignupInput } from '@langmail/shared-ui'

import { authClient, useSession } from '@/lib/auth-client'
import { isTestEnvironment } from '@/utils/environment'

const LANGMAIL_DOMAIN = 'langmail.me'

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: session, isPending } = useSession()
  const user = session?.user ?? null
  const loading = isPending

  const signInWithGoogle = async (opts?: { isReconnect?: boolean }) => {
    const callbackUrl = new URL(`${window.location.origin}/auth/callback/google`)
    if (opts?.isReconnect) {
      callbackUrl.searchParams.set('reconnect', 'true')
    }

    const { error } = await authClient.signIn.social({
      provider: 'google',
      callbackURL: callbackUrl.toString(),
      errorCallbackURL: callbackUrl.toString(),
      scopes: ['https://www.googleapis.com/auth/gmail.modify'],
    })

    return { error: error ? new Error(error.message ?? 'Google sign-in failed') : null }
  }

  const signInWithTestAccount = async () => {
    if (!isTestEnvironment()) {
      return { error: new Error('Test authentication is only available in development') }
    }

    const { error } = await authClient.signIn.email({
      email: 's.mitchell@techcorp.com',
      password: 'test123456',
    })

    return { error: error ? new Error(error.message ?? 'Test sign-in failed') : null }
  }

  const signInWithLangmail = async ({ email, password }: { email: string; password: string }) => {
    const { error } = await authClient.signIn.email({ email, password })
    return { error: error ? new Error(error.message ?? 'Sign-in failed') : null }
  }

  const signUpWithLangmail = async ({
    username,
    password,
    displayName,
    recoveryEmail,
    timeZone,
  }: StandaloneSignupInput) => {
    const { error } = await authClient.signUp.email({
      email: `${username}@${LANGMAIL_DOMAIN}`,
      password,
      name: displayName,
      // Optional; omit rather than send an empty string.
      recovery_email: recoveryEmail || undefined,
      calendar_time_zone: timeZone || undefined,
    })

    return { error: error ? new Error(error.message ?? 'Sign-up failed') : null }
  }

  const requestPasswordReset = async (email: string) => {
    const { error } = await authClient.requestPasswordReset({
      email,
      // Better Auth appends the token; this is where the emailed link lands.
      redirectTo: `${window.location.origin}/reset-password`,
    })

    return { error: error ? new Error(error.message ?? 'Password reset request failed') : null }
  }

  const resetPassword = async ({ token, newPassword }: { token: string; newPassword: string }) => {
    const { error } = await authClient.resetPassword({ newPassword, token })
    return { error: error ? new Error(error.message ?? 'Password reset failed') : null }
  }

  const signOut = async () => {
    await authClient.signOut()
  }

  const value = {
    user,
    session: session ?? null,
    loading,
    signInWithGoogle,
    signInWithTestAccount,
    signInWithLangmail,
    signUpWithLangmail,
    requestPasswordReset,
    resetPassword,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
